import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import * as process from 'process';
import { useIdleTimer } from 'react-idle-timer';

import API, { refreshAuth } from 'services/api';
import { createNotification } from 'components/ui';
import { useTenancy } from 'contexts/TenantContextProvider';
import { useNetworkCheck } from 'contexts/NetworkContextProvider';

type AuthContextType = {
  token: string | null;
  user: UserType | null;
  isLoading: boolean;
  setAuthToken: (token: string, refreshToken: string) => void;
  logout: () => void;
};
type AuthProviderProps = {
  children: ReactNode;
};

type UserType = {
  name: string;
  userId: number;
  userLevel: string;
  //avatarUrl: string | null;
  tenant_slug: string;
  tenant_assessment_version: number;
};

const ONE_MINUTE_IN_MS = 60000;
const TIMEOUT_MINUTES = Number(process.env.REACT_APP_IDLE_TIMEOUT_MINUTES || 10);

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [user, setUser] = useState<UserType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const tenantKey = useTenancy();
  const { isOnline } = useNetworkCheck();

  // Shared promise for token refresh to avoid multiple simultaneous refresh calls
  const refreshPromiseRef = useRef<Promise<{ token: string; refreshToken: string }> | null>(null);

  const showNotification = () => {
    createNotification({
      type: 'warning',
      title: 'Your session has timed out',
      description: 'Due to inactivity, your screen has been automatically locked. Please enter your ID to continue',
    });
  };

  const getUserInfoAndExpiryTime = (token: string) => {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));

    return {
      name: payload.display_name,
      userId: Number(payload.user_id),
      userLevel: payload.user_level,
      tenant_slug: payload.tenant_slug,
      tenant_assessment_version: parseInt(payload.tenant_assessment_version),
      expiryTime: payload.exp,
    };
  };

  // Check if token is expired or about to expire (within 30 seconds)
  const isTokenExpired = (token: string | null): boolean => {
    if (!token) return true;

    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const payload = JSON.parse(window.atob(base64));
      const expiryTime = payload.exp;

      // Add 30 second buffer before actual expiry
      const bufferTime = 30;
      const currentTime = Math.floor(Date.now() / 1000);

      return currentTime >= expiryTime - bufferTime;
    } catch (error) {
      console.error('Error checking token expiry:', error);
      return true;
    }
  };

  // Redirect to correct tenant URL if tenantKey doesn't match JWT tenant_slug
  const redirectToCorrectTenant = useCallback((correctTenantSlug: string) => {
    const domain = window.location.hostname.split('.').slice(1).join('.');
    const protocol = window.location.protocol;
    const currentPath = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;
    const urlToRedirect = `${protocol}//${correctTenantSlug}.${domain}${currentPath}${search}${hash}`;
    window.location.href = urlToRedirect;
  }, []);

  // When user goes offline, log them out
  useEffect(() => {
    if (!isOnline) {
      logout();
    }
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline]);

  // Logout user by destroying their cookies
  const logout = useCallback(() => {
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    delete API.defaults.headers.common['Authorization'];
    refreshPromiseRef.current = null; // Clear any pending refresh promise
  }, []);

  // Set Authorization header for Axios
  const setAuthAxiosHeader = (token: string): Promise<void> => {
    return new Promise(resolve => {
      API.defaults.headers.common['Authorization'] = 'Bearer ' + token;
      resolve();
    });
  };

  // Set Authorization token in memory, Axios Auth Header, and parse/load user info from token
  const setAuthToken = useCallback(async (token: string, refreshToken: string) => {
    await setAuthAxiosHeader(token);
    const userInfo = getUserInfoAndExpiryTime(token);
    setUser(userInfo);
    setToken(token);
    setRefreshToken(refreshToken);
  }, []);

  // Shared function to refresh token with promise reuse
  const performTokenRefresh = useCallback(
    async (currentRefreshToken: string): Promise<{ token: string; refreshToken: string }> => {
      // If a refresh is already in progress, return the existing promise
      if (refreshPromiseRef.current) {
        return refreshPromiseRef.current;
      }

      // Create new refresh promise
      refreshPromiseRef.current = (async () => {
        try {
          const response = await refreshAuth(currentRefreshToken);
          const { token: newToken, refreshToken: newRefreshToken } = response.data;

          await setAuthToken(newToken, newRefreshToken);

          return { token: newToken, refreshToken: newRefreshToken };
        } catch (error) {
          logout();
          showNotification();
          throw error;
        } finally {
          // Clear the promise after completion (success or failure)
          refreshPromiseRef.current = null;
        }
      })();

      return refreshPromiseRef.current;
    },
    [setAuthToken, logout],
  );

  // When user goes offline, log them out
  useEffect(() => {
    if (!isOnline) {
      logout();
    }
  }, [isOnline, logout]);

  // Set tenant key header for Axios
  const setTenantHeader = useCallback(() => {
    if (tenantKey) {
      API.defaults.headers.common['tenant-key'] = tenantKey;
    } else {
      delete API.defaults.headers.common['tenant-key'];
    }
  }, [tenantKey]);

  // Initialize auth - set tenant header and clear loading state
  useEffect(() => {
    setTenantHeader();
    setIsLoading(false);
  }, [tenantKey, setTenantHeader]);

  // Verify tenantKey matches JWT tenant_slug claim and redirect if mismatch
  useEffect(() => {
    if (!user || !tenantKey || !token) {
      return;
    }

    // Compare current URL tenantKey with JWT token tenant_slug
    if (tenantKey !== user.tenant_slug) {
      redirectToCorrectTenant(user.tenant_slug);
    }
  }, [user, tenantKey, token, redirectToCorrectTenant]);

  // Refresh token 1 minute before it expires
  useEffect(() => {
    const requestInterceptor = API.interceptors.request.use(
      async config => {
        // Skip token check for authentication endpoints
        if (config.url?.includes('/Authentication/') || config.url?.includes('/Health')) {
          return config;
        }

        // Check if token is expired or about to expire
        if (token && isTokenExpired(token) && refreshToken) {
          try {
            const { token: newToken } = await performTokenRefresh(refreshToken);
            // Update the authorization header for this request
            config.headers['Authorization'] = 'Bearer ' + newToken;
          } catch (error) {
            return Promise.reject(error);
          }
        }

        return config;
      },
      error => Promise.reject(error),
    );

    return () => {
      API.interceptors.request.eject(requestInterceptor);
    };
  }, [token, refreshToken, performTokenRefresh]);

  // Logout if user is idle
  useIdleTimer({
    timeout: TIMEOUT_MINUTES * ONE_MINUTE_IN_MS,
    onIdle: () => {
      if (token) {
        logout();
        showNotification();
      }
    },
  });

  // Automatically refresh token on 401 errors and retry failed request (fallback)
  useEffect(() => {
    const responseInterceptor = API.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;

        // Don't intercept if this is the refresh endpoint itself
        if (originalRequest.url?.includes('/Authentication/Renew')) {
          // If refresh endpoint fails with 401, logout immediately
          if (error.response && error.response.status === 401) {
            logout();
          }
          return Promise.reject(error);
        }

        // Check if error is 401 and we haven't already tried to refresh
        if (error.response && error.response.status === 401 && !originalRequest._retry && refreshToken) {
          originalRequest._retry = true;

          try {
            const { token: newToken } = await performTokenRefresh(refreshToken);

            // Update the authorization header for the failed request
            originalRequest.headers['Authorization'] = 'Bearer ' + newToken;

            // Retry the original request
            return API(originalRequest);
          } catch (refreshError) {
            // If token refresh fails, logout the user
            logout();
            return Promise.reject(refreshError);
          }
        }

        // If 401 and no refresh token available, logout
        if (error.response && error.response.status === 401) {
          logout();
        }

        return Promise.reject(error);
      },
    );

    return () => {
      API.interceptors.response.eject(responseInterceptor);
    };
  }, [refreshToken, performTokenRefresh, logout]);

  // Setup Context for Auth
  const contextValue = useMemo(
    () => ({
      token,
      user,
      isLoading,
      setAuthToken,
      logout,
    }),
    [token, user, isLoading, setAuthToken, logout],
  );

  return <AuthContext.Provider value={contextValue}>{!isLoading && children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  return context;
};
